import { useEffect, useRef, useState } from 'react';
import type { CardInfo, FullCardInfo } from '@lub0v/santa-utils';
import { santaClient } from '@src/state/client';
import { useTheme } from '@src/theme/useTheme';

export type RevealIdentity = Pick<CardInfo, 'id' | 'username' | 'picture' | 'avatar' | 'frameName'>;
export interface RevealFrame {
  person: RevealIdentity;
  step: number;
}

// A quick flutter, easing out into a deliberate false stop before the giftee.
const SLOWDOWN = [80, 85, 100, 130, 170, 220, 290, 380, 500, 650, 850, 1100, 1650];

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    signal.addEventListener('abort', finish, { once: true });
    if (signal.aborted) finish();
  });
}

function preloadArtwork(
  people: RevealIdentity[],
  artwork?: Readonly<Partial<Record<string, string>>>,
) {
  return Promise.all(
    people.map((person) => {
      const src = person.picture === 'custom_avatar' ? person.avatar : artwork?.[person.picture];
      if (!src) return Promise.resolve();
      const image = new Image();
      image.src = src;
      return image.decode().catch(() => {});
    }),
  );
}

/** The reel is presentation only; its destination always comes from getWard. */
export function useGifteeReveal({
  boxKey,
  card,
  cards,
  onChanged,
}: {
  boxKey: string;
  card: FullCardInfo | null;
  cards: CardInfo[];
  onChanged: () => Promise<void> | void;
}) {
  const { seasonPackage } = useTheme();
  const [busy, setBusy] = useState(false);
  const [portraits, setPortraits] = useState<RevealIdentity[]>([]);
  const [suspense, setSuspense] = useState(false);
  const [frame, setFrame] = useState<RevealFrame | null>(null);
  const [result, setResult] = useState<FullCardInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [celebrating, setCelebrating] = useState(false);
  const run = useRef<AbortController | null>(null);

  useEffect(() => () => run.current?.abort(), [boxKey, card?.id]);

  useEffect(() => {
    if (!card?.isWardKnown)
      void preloadArtwork(cards.slice(0, 32), seasonPackage.participantPictures);
  }, [cards, card?.isWardKnown, seasonPackage.participantPictures]);

  useEffect(() => {
    if (!celebrating) return;
    const timer = setTimeout(() => setCelebrating(false), 4200);
    return () => clearTimeout(timer);
  }, [celebrating]);

  async function reveal() {
    if (!card || run.current) return;
    const controller = new AbortController();
    const { signal } = controller;
    run.current = controller;
    setBusy(true);
    setError(null);
    setSuspense(false);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const deck = cards.filter((person) => !person.isYou && person.id !== card.id);
    for (let i = deck.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    // Keep the cosmetic reel bounded even for games with thousands of people.
    deck.splice(32);
    setPortraits(deck);
    const artworkReady = preloadArtwork(deck.slice(0, 32), seasonPackage.participantPictures);
    let step = 0;
    const advance = (avoidId?: number) => {
      let person = deck[step % deck.length];
      if (person?.id === avoidId) person = deck[(step + 1) % deck.length];
      if (person) setFrame({ person, step: step++ });
    };
    // Keep the fast phase going if the network is slow; never land on a guess.
    const outcome: { person?: FullCardInfo; error?: string } = {};
    const request = (async () => {
      try {
        const saved = await santaClient.saveCard({
          key: boxKey,
          username: card.username,
          isWardKnown: true,
        });
        if (signal.aborted) return;
        if (!saved.ok) {
          outcome.error = saved.error?.message || 'errors.UNKNOWN';
          return;
        }
        const response = await santaClient.getWard({ key: boxKey });
        if (response.ok && response.data) outcome.person = response.data;
        else outcome.error = response.error?.message || 'errors.UNKNOWN';
      } catch {
        outcome.error = 'errors.UNKNOWN';
      }
    })();

    try {
      if (!reducedMotion) {
        // Cached artwork normally resolves immediately; a failed/slow upload
        // must not hold the reveal indefinitely.
        // Let the 800ms entrance finish, then hold the card for another 700ms.
        await Promise.all([Promise.race([artworkReady, pause(1200, signal)]), pause(1500, signal)]);
        if (signal.aborted) return;
      }
      if (!reducedMotion && deck.length > 1) {
        // At least a few quick passes before easing out, even on a local response.
        do {
          advance();
          await pause(80, signal);
          if (signal.aborted) return;
        } while ((!outcome.person && !outcome.error) || (step < 6 && !outcome.error));
      } else {
        await request;
      }
      if (signal.aborted) return;
      if (outcome.error || !outcome.person) {
        setError(outcome.error || 'errors.UNKNOWN');
        return;
      }
      const destination = outcome.person;
      setPortraits((current) => [
        ...current.filter((person) => person.id !== destination.id),
        destination,
      ]);
      if (!reducedMotion && deck.length > 1) {
        for (let i = 0; i < SLOWDOWN.length; i += 1) {
          const last = i === SLOWDOWN.length - 1;
          setSuspense(last);
          advance(last ? outcome.person.id : undefined);
          await pause(SLOWDOWN[i], signal);
          if (signal.aborted) return;
        }
      }
      await Promise.race([
        preloadArtwork([outcome.person], seasonPackage.participantPictures),
        pause(700, signal),
      ]);
      if (signal.aborted) return;
      setResult(outcome.person);
      setCelebrating(!reducedMotion);
      // Refresh shared state only after landing, so incoming props cannot spoil
      // the reel. Keep the fetched identity visible while refreshed props arrive.
      void Promise.resolve()
        .then(onChanged)
        .catch(() => {});
    } finally {
      if (!signal.aborted) {
        setFrame(null);
        setBusy(false);
        run.current = null;
      }
    }
  }

  function cancel() {
    run.current?.abort();
    run.current = null;
    setBusy(false);
    setFrame(null);
    setSuspense(false);
    setCelebrating(false);
  }

  return { busy, suspense, portraits, frame, result, error, celebrating, reveal, cancel };
}
